import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TokenStorageService } from './token-storage.service';
import { TokenRefreshService } from './token-refresh.service';

@Injectable({
  providedIn: 'root',
})
export class TokenExpirationMonitorService implements OnDestroy {
  private tokenStorage = inject(TokenStorageService);
  private tokenRefreshService = inject(TokenRefreshService);
  private router = inject(Router);

  private monitorSubscription: Subscription | null = null;
  private readonly CHECK_INTERVAL = 60000; // Verificar cada 60 segundos
  private readonly REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // Refrescar 5 minutos antes de expirar
  private readonly MAX_REFRESH_ATTEMPTS = 3; // Intentar refrescar 3 veces antes de cerrar sesión
  private refreshAttempts = 0;
  private isRefreshing = false;

  /**
   * Inicia el monitoreo de expiración del token
   */
  startMonitoring(): void {
    this.stopMonitoring();
    this.refreshAttempts = 0;

    // Verificar inmediatamente al iniciar
    this.checkTokenExpiration();

    // Luego verificar periódicamente
    this.monitorSubscription = interval(this.CHECK_INTERVAL)
      .pipe(filter(() => this.tokenStorage.hasToken()))
      .subscribe(() => {
        this.checkTokenExpiration();
      });
  }

  /**
   * Detiene el monitoreo de expiración del token
   */
  stopMonitoring(): void {
    if (this.monitorSubscription) {
      this.monitorSubscription.unsubscribe();
      this.monitorSubscription = null;
    }
  }

  /**
   * Verifica si el token ha expirado o está por expirar y toma acción
   */
  private checkTokenExpiration(): void {
    // Si ya estamos refrescando, no hacer nada
    if (this.isRefreshing) {
      return;
    }

    const timeRemaining = this.tokenStorage.getTimeUntilExpiration();
    const minutesRemaining = Math.floor(timeRemaining / 60000);

    if (this.tokenStorage.isTokenExpired()) {
      // Token ya expiró - intentar refrescar
      console.warn('⏰ Token expirado. Intentando refrescar...');
      this.attemptTokenRefresh();
    } else if (timeRemaining <= this.REFRESH_BEFORE_EXPIRY_MS && timeRemaining > 0) {
      // Token está por expirar - refrescar proactivamente
      console.warn(
        `⚠️ Token expirará en ${minutesRemaining} minuto(s). Refrescando automáticamente...`,
      );
      this.attemptTokenRefresh();
    } else if (minutesRemaining <= 10 && minutesRemaining > 5) {
      // Advertir al usuario cuando quedan entre 10 y 5 minutos
      console.info(
        `ℹ️ Su sesión expirará en ${minutesRemaining} minuto(s). Se renovará automáticamente.`,
      );
    }
  }

  /**
   * Intenta refrescar el token. Si falla después de MAX_REFRESH_ATTEMPTS, cierra sesión.
   */
  private attemptTokenRefresh(): void {
    // Verificar si hay refresh token disponible
    if (!this.tokenStorage.getRefreshToken()) {
      console.warn('⚠️ No hay refresh token disponible. Cerrando sesión...');
      this.handleTokenExpiration();
      return;
    }

    this.isRefreshing = true;
    this.refreshAttempts++;

    console.log(`🔄 Intento de refresh ${this.refreshAttempts}/${this.MAX_REFRESH_ATTEMPTS}...`);

    this.tokenRefreshService.refreshToken().subscribe({
      next: () => {
        console.log('✅ Token refrescado exitosamente');
        this.isRefreshing = false;
        this.refreshAttempts = 0; // Resetear contador en éxito
      },
      error: (error) => {
        console.error(
          `❌ Error al refrescar token (intento ${this.refreshAttempts}/${this.MAX_REFRESH_ATTEMPTS}):`,
          error,
        );
        this.isRefreshing = false;

        if (this.refreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
          console.error(
            `🚫 Se agotaron los ${this.MAX_REFRESH_ATTEMPTS} intentos de refresh. Cerrando sesión...`,
          );
          this.handleTokenExpiration();
        } else {
          // Esperar un poco antes del próximo intento
          const retryDelay = this.refreshAttempts * 2000; // 2s, 4s, 6s
          console.log(`⏳ Reintentando en ${retryDelay / 1000} segundos...`);
          setTimeout(() => {
            this.attemptTokenRefresh();
          }, retryDelay);
        }
      },
    });
  }

  /**
   * Maneja la expiración del token cerrando sesión y redirigiendo al login
   */
  private handleTokenExpiration(): void {
    // Limpiar token expirado
    this.tokenStorage.clearToken();

    // Detener monitoreo
    this.stopMonitoring();

    // Resetear intentos
    this.refreshAttempts = 0;
    this.isRefreshing = false;

    // Limpiar permisos y usuario de localStorage
    localStorage.removeItem('user_permissions');
    localStorage.removeItem('auth_user');

    // Redirigir al login
    this.router.navigate(['/login'], {
      queryParams: { sessionExpired: 'true' },
    });
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }
}
